import {
  CITATION_MARKER_SOURCE,
  LEADING_BLANKS_MAX,
  mergeAdjacentCitationMarkers,
  readCitationMarkerIds,
  TRAILING_INCOMPLETE_CITATION_MARKER,
} from "@shaarp/common";

/** The punctuation a pill is moved after: what ends a sentence or a clause. */
const TRAILING_PUNCTUATION = "[.,;:!?…]+";

/**
 * Bridges the assistant's citation markers and Streamdown's custom-tag
 * rendering, the way `company-mentions.ts` does for companies:
 *   `[@shaarp/cite ids="w0123456789abcdef"]` → `<citation ids="w0123456789abcdef">`
 *
 * Streamdown renders the element through `CitationPill`, which resolves the ids
 * against the conversation's sources. The ids are the only thing that reaches
 * the element, and the id pattern keeps it to `[0-9a-f]`, so nothing the model
 * wrote can break out of the attribute. A marker that is still being streamed
 * is dropped at the end of the text rather than flashed as raw syntax; one with
 * no valid id renders nothing.
 *
 * A pill comes after the punctuation it follows in the text, the way ChatGPT
 * places its sources: `phrase [marker].` renders as `phrase. [pill]`. The
 * prompt asks for that order; the swap here keeps older answers, and a model
 * that writes the marker first anyway, from showing a pill before the period.
 *
 * Markers in a row become one element: a sentence carries a single pill. The
 * patterns are `@shaarp/common`'s, so the pills hold exactly the ids
 * `collectCitedSourceIds` counts.
 *
 * The text-only reading (a copy, an email body) is `stripCitationMarkers` in
 * `@shaarp/common`.
 */
export function transformCitationMarkers(text: string): string {
  if (!text.includes("[@shaarp/cite")) return text;

  const merged = mergeAdjacentCitationMarkers(
    text.replace(TRAILING_INCOMPLETE_CITATION_MARKER, ""),
  ).replace(
    new RegExp(
      `[ \\t]{0,${LEADING_BLANKS_MAX}}(${CITATION_MARKER_SOURCE})([ \\t\\u00a0\\u202f]*${TRAILING_PUNCTUATION})`,
      "g",
    ),
    // The blank a French "?" or ":" takes before it moves with it.
    (_match, marker: string, _attributes: string, punctuation: string) =>
      `${punctuation} ${marker}`,
  );
  return merged.replace(
    new RegExp(CITATION_MARKER_SOURCE, "g"),
    (_marker, attributes: string) => {
      const ids = readCitationMarkerIds(attributes);
      return ids.length > 0
        ? `<citation ids="${ids.join(" ")}">source</citation>`
        : "";
    },
  );
}
